/**
 * Hook for managing Flywheel configuration
 * Reads the org's Flywheel flow via GraphQL (GET_FLYWHEEL_FLOW — server-side
 * filtered by flowType so we only pull one row instead of every flow on the
 * org), writes via Remi API.
 */

import { useQuery as useApolloQuery, useApolloClient } from '@apollo/client/react';
import { useMutation } from '@tanstack/react-query';
import { GET_FLYWHEEL_FLOW } from '../../lib/graphql/queries/organizations';
import { updateOrganization, createFlow, updateFlow } from '../../api/remiApi';
import { DEFAULT_KNOWLEDGEBASE_ID, DAYS_OF_WEEK } from '../../constants/flywheel';

// GQL FlowSummary subset needed for flywheel
interface GQLFlowSummary {
  flowId: string;
  flowType: string | null;
  transferNumbers: string | null;
  allowedHours: string | null;
  allowedDays: string | null;
  timezone: string | null;
  countryCode: string | null;
  excludeHolidays: boolean | null;
  deletedAt: string | null;
}

interface GetFlywheelFlowData {
  flows: {
    flows: GQLFlowSummary[];
  };
}

function tryParseJSON<T>(value: string | null | undefined, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

export interface FlywheelFlowData {
  flowId: string;
  transfer_numbers: Record<string, string>;
  allowed_hours: { start_hour: number; end_hour: number };
  allowed_days: string[];
  timezone: string;
  country_code: string;
  exclude_holidays: boolean;
}

/**
 * Fetch flywheel flow data for an organization via GraphQL.
 * Pass `enabled: false` (e.g. when the modal is closed) to skip the query —
 * Apollo otherwise fires it as soon as the hook mounts, which spams the
 * GQL endpoint with a flows fetch for every selected org in ClientAdmin.
 */
export const useFlywheelFlow = (
  organizationId: string | undefined,
  options: { enabled?: boolean } = {}
) => {
  const { enabled = true } = options;
  const { data, loading } = useApolloQuery<GetFlywheelFlowData>(GET_FLYWHEEL_FLOW, {
    variables: { organizationId: organizationId || '' },
    skip: !organizationId || !enabled,
    fetchPolicy: 'cache-and-network',
  });

  const flywheelFlow = (data?.flows?.flows ?? []).find(
    (f) => f.flowType === 'Flywheel' && !f.deletedAt
  );

  const flowData: FlywheelFlowData | null = flywheelFlow
    ? {
        flowId: flywheelFlow.flowId,
        transfer_numbers: tryParseJSON<Record<string, string>>(flywheelFlow.transferNumbers, {}),
        allowed_hours: tryParseJSON<{ start_hour: number; end_hour: number }>(
          flywheelFlow.allowedHours,
          { start_hour: 9, end_hour: 20 }
        ),
        allowed_days: tryParseJSON<string[]>(flywheelFlow.allowedDays, [...DAYS_OF_WEEK]),
        timezone: flywheelFlow.timezone ?? '',
        country_code: flywheelFlow.countryCode ?? 'US',
        exclude_holidays: flywheelFlow.excludeHolidays ?? true,
      }
    : null;

  return { data: flowData, isLoading: loading };
};

interface SaveFlywheelConfigParams {
  organizationId: string;
  organizationName: string;
  flywheelEnabled: boolean;
  transferNumbers: Record<string, string>;
  startHour: number;
  endHour: number;
  selectedDays: string[];
  timezone: string;
  countryCode: string;
  excludeHolidays: boolean;
}

/**
 * Save flywheel configuration via Remi API
 */
export const useSaveFlywheelConfig = () => {
  const apolloClient = useApolloClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      organizationName,
      flywheelEnabled,
      transferNumbers,
      startHour,
      endHour,
      selectedDays,
      timezone,
      countryCode,
      excludeHolidays,
    }: SaveFlywheelConfigParams) => {
      // 1. Update organization settings
      await updateOrganization(organizationId, {
        settings: { flywheel_enabled: flywheelEnabled },
      });

      // 2. Create/update flywheel flow if enabling
      if (flywheelEnabled) {
        // Check Apollo cache for existing flywheel flow
        const cached = apolloClient.readQuery<GetFlywheelFlowData>({
          query: GET_FLYWHEEL_FLOW,
          variables: { organizationId },
        });
        const existingFlow = (cached?.flows?.flows ?? []).find(
          (f) => f.flowType === 'Flywheel' && !f.deletedAt
        );

        const flowPayload = {
          name: `${organizationName} - Flywheel`,
          flow_type: 'Flywheel',
          status: 'active',
          transfer_numbers: transferNumbers,
          allowed_hours: { start_hour: startHour, end_hour: endHour },
          allowed_days: selectedDays,
          timezone,
          country_code: countryCode,
          exclude_holidays: excludeHolidays,
          prompt_parameters: { general_knowledgebase_id: DEFAULT_KNOWLEDGEBASE_ID },
          use_round_robin: false,
          rejoin: false,
        };

        if (existingFlow) {
          await updateFlow(organizationId, existingFlow.flowId, flowPayload);
        } else {
          await createFlow(organizationId, flowPayload);
        }
      }

      return { success: true, organizationId, flywheelEnabled };
    },
    onSuccess: ({ organizationId, flywheelEnabled }) => {
      // Update the org's settings.flywheel_enabled directly in Apollo cache
      // so the UI reflects the change without a page reload.
      apolloClient.cache.modify({
        id: apolloClient.cache.identify({ __typename: 'Organization', organizationId }),
        fields: {
          settings(existing: Record<string, unknown> | null) {
            return { ...(existing ?? {}), flywheel_enabled: flywheelEnabled };
          },
        },
      });
      // Refetch flows so the updated flywheel flow is reflected in the cache
      apolloClient.refetchQueries({ include: [GET_FLYWHEEL_FLOW] });
    },
  });
};

interface VerifyFlywheelConfigParams {
  organizationId: string;
  orgEnabled: boolean;
}

interface VerifyFlywheelConfigResult {
  orgEnabled: boolean;
  flowExists: boolean;
  flowTypeCorrect: boolean;
}

/**
 * Verify flywheel configuration is correctly set up
 */
export const useVerifyFlywheelConfig = () => {
  const apolloClient = useApolloClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      orgEnabled,
    }: VerifyFlywheelConfigParams): Promise<VerifyFlywheelConfigResult> => {
      // Fresh fetch of flows to verify flywheel flow exists
      const { data } = await apolloClient.query<GetFlywheelFlowData>({
        query: GET_FLYWHEEL_FLOW,
        variables: { organizationId },
        fetchPolicy: 'network-only',
      });

      const flows = data?.flows?.flows ?? [];
      const flywheelFlow = flows.find((f) => f.flowType === 'Flywheel' && !f.deletedAt);

      return {
        orgEnabled,
        flowExists: !!flywheelFlow,
        flowTypeCorrect: flywheelFlow?.flowType === 'Flywheel',
      };
    },
  });
};

/**
 * Get default flywheel config values
 */
export const getDefaultFlywheelConfig = (orgTimezone?: string) => ({
  flywheelEnabled: false,
  transferLabel: 'Office',
  transferNumber: '',
  startHour: 9,
  endHour: 20,
  selectedDays: [...DAYS_OF_WEEK] as string[],
  timezone: orgTimezone || 'UTC',
  excludeHolidays: true,
});

export { DAYS_OF_WEEK };
